<template>
  <BModal
    ref="modal"
    size="lg"
    :title="likeOnly ? $gadTranslateUpper('general_register_like_modal_title') : $gadTranslateUpper('vacancy_subscription_modal_im_interested')"
    :cancel-title="$gadTranslateUpper('general_button_cancel')"
    :ok-title="likeOnly ? $gadTranslateUpper('general_button_save') : $gadTranslateUpper('general_button_send')"
    :ok-disabled="subscribing || (!manualRegistration && !$auth.user)"
    :cancel-disabled="subscribing"
    @ok="okClicked"
    @cancel="cancelClicked"
    @shown="focusMessage"
  >
    <ValidationObserver
      ref="validationObserver"
      v-slot="validationObserverProps"
    >
      <!-- After an SSO login, the user is already registered, so we only ask them to fill in the custom registration questions. -->
      <template v-if="ssoSubscribedAndShowCustomFields">
        <CustomFields
          v-if="customFields && customFields.length > 0"
          :fields="customFields"
          :custom-fields-data="customFieldsData"
          :show-title="true"
          :autofocus-first="true"
        />
        <FormHasErrorsWarning :validation-observer-props="validationObserverProps" />
      </template>

      <template v-else>
        <template v-if="!$auth.user && manualRegistration">
          <p>
            {{ likeOnly ? $gadTranslateUpper('general_register_like_modal_text') : $gadTranslateUpper('one_day_vacancy_subscription_modal_login_first') }}
          </p>
          <div class="text-center">
            <BButton
              variant="primary"
              @click="login"
            >
              {{ $gadTranslateUpper('general_button_log_in') }}
            </BButton>
          </div>
          <TextOnLine class="mt-4 mb-2">{{ $gadTranslateUpper('one_day_vacancy_subscription_modal_register_as_volunteer') }}</TextOnLine>
        </template>

        <SocialMediaLoginButtons
          v-show="ssoEnabledForOneDayVacancies"
          :show-text-on-line="manualRegistration"
          :gad-redirect-uri="gadRedirectUri"
          :is-subscribing="true"
          @fetched-social-media="fetchedSocialMedia"
        />

        <div v-if="manualRegistration || $auth.user">
          <template v-if="!likeOnly">
            <p>{{ $gadTranslateUpper('one_day_vacancy_subscription_modal_why_interested', { organisationName: oneDayVacancy.organisationName }) }}:</p>

            <FormControlWrapper
              v-slot="{state}"
              :rules="{required: true, min: 10, max: 800}"
              class="mb-4"
            >
              <BTextarea
                id="messageInput"
                v-model="message"
                :placeholder="$gadTranslate('one_day_vacancy_subscription_modal_why_interested_placeholder')"
                :state="state"
              />
            </FormControlWrapper>

            <CustomFields
              v-if="customFields && customFields.length > 0"
              :fields="customFields"
              :custom-fields-data="customFieldsData"
              :show-title="true"
              class="mb-4"
            />
          </template>

          <template v-if="!$auth.user">
            <p>{{ $gadTranslateUpper('one_day_vacancy_subscription_modal_enter_own_details') }}</p>
            <VolunteerRegistrationData
              :user="user"
              :show-phone="true"
              :show-password="false"
              :show-terms="false"
              :allow-existing-email="true"
              :set-auto-focus="false"
            />
          </template>

          <template v-if="!likeOnly && amountSubscriptionOptions.length > 1">
            <FormControlWrapper
              v-slot="{state}"
              :rules="{required: true}"
              :label="$gadTranslateUpper('one_day_vacancy_subscription_modal_how_many_subscriptions_label')"
              class="mb-0"
            >
              <TranslatedMultiselect
                v-model="amountSubscriptions"
                :options="amountSubscriptionOptions"
                :state="state"
                :allow-empty="false"
                :multiple="false"
                :placeholder="$gadTranslate('one_day_vacancy_subscription_modal_how_many_subscriptions_placeholder')"
              />
            </FormControlWrapper>

            <Accordion
              v-model:activeIndex="activeAccordionIndex"
              class="collapse-component mb-3"
            >
              <AccordionTab
                v-for="(otherUser, index) in otherUsers"
                :key="index"
              >
                <template #header>
                  <span>{{ otherUser.firstName || otherUser.familyName ? `${otherUser.firstName || ''} ${otherUser.familyName || ''}`: `${$gadTranslateUpper('general_person')} ${index + 1}` }}</span>
                </template>

                <VolunteerRegistrationData
                  :user="otherUser"
                  :show-phone="true"
                  :show-password="false"
                  :show-terms="false"
                  :allow-existing-email="true"
                  :set-auto-focus="false"
                />

                <CustomFields
                  v-if="customFields && customFields.length > 0"
                  :fields="customFields"
                  :custom-fields-data="otherUser.customFieldsData"
                  :show-title="true"
                  class="mt-4"
                />
              </AccordionTab>
            </Accordion>
          </template>

          <FormControlWrapper
            v-slot="{state}"
            :rules="{required: true, isChecked: true}"
            class="mt-5"
            :form-field-value="termsAccepted"
          >
            <AcceptTermsCheckbox
              v-model="termsAccepted"
              :state="state"
            />
          </FormControlWrapper>

          <Recaptcha ref="recaptcha" />
          <FormHasErrorsWarning :validation-observer-props="validationObserverProps" />
        </div>
      </template>
    </ValidationObserver>
  </BModal>
</template>

<script setup>
/* global useNuxtApp */
import { ref, watch, computed, defineExpose, nextTick, onBeforeUnmount, onMounted } from 'vue'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import { createEmptyVolunteerForRegistration } from 'give-a-day-shared/data/emptyObjects.mjs'
import { logGoogleAnalyticsEvent } from '~/functions/googleAnalyticsEvent.js'
import { returnGadBaseUrl } from '@/functions/domains.js'

/**
 * Builds the redirect URI for returning to the ODV after a successful SSO login.
 *
 * Appends a `ssoLogin=true` query parameter so the app can detect that the user just logged in via SSO.
 *
 * @param {string} fullPath - The relative path of the current page (e.g. from Vue router).
 * @returns {string} The absolute redirect URL including the `ssoLogin=true` parameter.
 */
function buildGadRedirectUri (fullPath) {
  const url = new URL(fullPath, returnGadBaseUrl())
  url.searchParams.set('ssoLogin', true)
  return url.toString()
}

const emit = defineEmits(['form-submitted', 'user-did-subscribe', 'like-success'])

const nuxtApp = useNuxtApp()
const router = useRouter()

// Snapshot fullPath into a local ref instead of reading the reactive route
// proxy inside the gadRedirectUri computed. Prevents the computed's effect
// from retaining the route proxy (and via it the SSR H3Event) after render.
const currentFullPath = ref(router.currentRoute.value.fullPath)
let unregisterAfterEach = () => {}
if (process.client) {
  unregisterAfterEach = router.afterEach((to) => {
    currentFullPath.value = to.fullPath
  })
}
onBeforeUnmount(() => {
  unregisterAfterEach()
})

const recaptcha = ref()
const socialMedia = ref(null)
const modal = ref()
const validationObserver = ref()
const amountSubscriptions = ref(0)
const amountSubscriptionOptions = ref([])

const message = ref()
const customFields = ref([])
const customFieldsData = ref({})
const user = ref(createEmptyVolunteerForRegistration())
const otherUsers = ref([])
const activeAccordionIndex = ref(0)
const termsAccepted = ref()
const subscribing = ref(false)
const ssoSubscribedAndShowCustomFields = ref(false)
const gadRedirectUri = computed(() => buildGadRedirectUri(currentFullPath.value))
const manualRegistration = computed(() => !!(socialMedia.value?.manualRegistration ?? true))
const ssoEnabledForOneDayVacancies = computed(() => socialMedia.value?.ssoEnabledForOneDayVacancies ?? false)

const props = defineProps({
  oneDayVacancy: {
    type: Object,
    required: true,
  },
  likeOnly: {
    type: Boolean,
    default: false,
  },
})

if (props.oneDayVacancy.id) {
  const diff = props.oneDayVacancy.maxAmountSubscriptions - props.oneDayVacancy.amountSubscriptions
  for (let i = 0; i < diff; i++) {
    amountSubscriptionOptions.value.push(i)
  }
}

onMounted(async () => {
  if (!props.likeOnly && props.oneDayVacancy.id) {
    customFields.value = await nuxtApp.$getFromBackend('/fetchCustomFieldsForOneDayVacancy', { targetGroupId: 7, oneDayVacancyId: props.oneDayVacancy.id })
  }
})

const show = () => {
  modal.value.show()
}

const login = async () => {
  const oneDayVacancyUrl = props.likeOnly
    ? nuxtApp.$oneDayVacancyUrl(props.oneDayVacancy, null, false, true)
    : nuxtApp.$oneDayVacancyUrl(props.oneDayVacancy, null, true)
  await router.push(nuxtApp.$findPath('/login', null, null, { redirectUrl: oneDayVacancyUrl }))
}

watch(amountSubscriptions, (newVal) => {
  const count = Number(newVal) || 0

  if (count > otherUsers.value.length) {
    for (let i = otherUsers.value.length; i < count; i++) {
      // Each extra person gets their own answers object so they can fill in the custom questions individually.
      otherUsers.value.push({ ...createEmptyVolunteerForRegistration(), customFieldsData: {} })
    }
  } else if (count < otherUsers.value.length) {
    otherUsers.value.splice(count)
  }
})

const cancelClicked = () => {
  reset()
}

const reset = () => {
  message.value = null
  customFieldsData.value = {}
  amountSubscriptions.value = 0
  activeAccordionIndex.value = 0
  user.value = createEmptyVolunteerForRegistration()
  otherUsers.value = [{ ...createEmptyVolunteerForRegistration(), customFieldsData: {} }]
  subscribing.value = false
  ssoSubscribedAndShowCustomFields.value = false
}

function handleAPIError (error) {
  if (error.errorKey === 'one_day_vacancy_does_not_exist') {
    nuxtApp.$swal({
      title: nuxtApp.$gadTranslate('one_day_vacancy_does_not_exist_swal_title'),
      text: nuxtApp.$gadTranslate('one_day_vacancy_does_not_exist_swal_text'),
      icon: 'error',
      showCancelButton: false,
      confirmButtonText: nuxtApp.$gadTranslateUpper('general_ok'),
    })
  } else if (error.errorKey === 'one_day_vacancy_inactive') {
    nuxtApp.$swal({
      title: nuxtApp.$gadTranslate('one_day_vacancy_inactive_swal_title'),
      text: nuxtApp.$gadTranslate('one_day_vacancy_inactive_swal_text'),
      icon: 'error',
      showCancelButton: false,
      confirmButtonText: nuxtApp.$gadTranslateUpper('general_ok'),
    })
  } else if (error.errorKey === 'one_day_vacancy_other_users_does_not_equal_amount_subscriptions') {
    nuxtApp.$swal({
      title: nuxtApp.$gadTranslate('one_day_vacancy_other_users_does_not_equal_amount_subscriptions_swal_title'),
      text: nuxtApp.$gadTranslate('one_day_vacancy_other_users_does_not_equal_amount_subscriptions_swal_text'),
      icon: 'error',
      showCancelButton: false,
      confirmButtonText: nuxtApp.$gadTranslateUpper('general_ok'),
    })
  } else if (error.errorKey === 'one_day_vacancy_subscription_not_enough_places') {
    nuxtApp.$swal({
      title: nuxtApp.$gadTranslate('one_day_vacancy_subscription_not_enough_places_swal_title'),
      text: nuxtApp.$gadTranslate('one_day_vacancy_subscription_not_enough_places_swal_text'),
      icon: 'error',
      showCancelButton: false,
      confirmButtonText: nuxtApp.$gadTranslateUpper('general_ok'),
    })
  } else {
    // We don't know how to handle this error. Let's throw it again so we can show the generic error message
    throw error
  }
}

const okClicked = async (event) => {
  event.preventDefault()
  subscribing.value = true

  let valid = await validationObserver.value.validate()
  if (!valid) {
    subscribing.value = false
    return
  }

  // SSO flow: the user is already logged in and only fills in the custom registration questions.
  if (ssoSubscribedAndShowCustomFields.value) {
    await doLoggedInSubscribe()
    return
  }

  try {
    let recaptchaToken = await recaptcha.value.getToken()
    let params = {
      recaptchaToken,
      oneDayVacancyId: props.oneDayVacancy.id,
    }
    if (!props.likeOnly) {
      params.message = message.value
      params.otherUsers = otherUsers.value
    }
    if (!nuxtApp.$auth.user) {
      params.user = { ...user.value, customFieldsData: customFieldsData.value }
    } else {
      params.user = {
        firstName: nuxtApp.$auth.user.firstName,
        familyName: nuxtApp.$auth.user.familyName,
        email: nuxtApp.$auth.user.email,
        phone: nuxtApp.$auth.user.phone,
        customFieldsData: customFieldsData.value,
      }
    }
    if (props.likeOnly) {
      await nuxtApp.$postToBackend('/createVolunteerAndLikeOneDayVacancy', params)
    } else {
      await nuxtApp.$postToBackend('/createVolunteerAndSubscribeToOneDayVacancy', params)
    }

    // Subscription success!
    if (props.likeOnly) {
      emit('like-success')
    } else {
      emit('form-submitted', otherUsers.value.length + 1)
      logGoogleAnalyticsEvent('volunteer_subscribed_to_one_day_vacancy', 'subscribe_to_one_day_vacancy', nuxtApp.$i18n.locale)
      nuxtApp.$swal({
        title: nuxtApp.$gadTranslate('one_day_vacancy_subscription_subscription_success'),
        html: nuxtApp.$gadTranslate('one_day_vacancy_subscription_subscription_success_explanation'),
        icon: 'success',
        allowOutsideClick: false,
        showCancelButton: false,
        confirmButtonText: nuxtApp.$gadTranslateUpper('general_ok'),
      })
    }
    modal.value.hide()
    reset()
  } catch (error) {
    recaptcha.value.reset()
    subscribing.value = false
    modal.value.hide()
    handleAPIError(error)
  }
}

// Subscribes an already logged-in (SSO) user, sending along any custom registration answers.
async function doLoggedInSubscribe () {
  try {
    await nuxtApp.$postToBackend('/subscribeToOneDayVacancy', {
      oneDayVacancyId: props.oneDayVacancy.id,
      customFieldsData: customFieldsData.value,
    })
  } catch (err) {
    subscribing.value = false
    modal.value.hide()
    reset()
    handleAPIError(err)
    return
  }

  // Notify parent component that user subscribed
  emit('user-did-subscribe')

  modal.value.hide()
  reset()

  nuxtApp.$swal({
    title: nuxtApp.$gadTranslateUpper('general_swal_success_title'),
    html: nuxtApp.$gadTranslateUpper('one_day_vacancy_detail_user_subscribed_success_swal_text'),
    icon: 'success',
    showCancelButton: false,
    confirmButtonText: nuxtApp.$gadTranslateUpper('general_ok'),
  })
}

async function handleSSOSubscribe (oneDayVacancy) {
  if (!nuxtApp.$auth.user) {
    return
  }

  // Load the custom registration questions linked to this ODV.
  customFields.value = await nuxtApp.$getFromBackend('/fetchCustomFieldsForOneDayVacancy', { targetGroupId: 7, oneDayVacancyId: oneDayVacancy.id })

  // No custom questions to answer: subscribe right away (original one-click behaviour).
  if (!customFields.value || customFields.value.length === 0) {
    await doLoggedInSubscribe()
    return
  }

  // Custom questions required: show the modal so the (already logged-in) user can fill them in before subscribing.
  ssoSubscribedAndShowCustomFields.value = true
  modal.value.show()
}

const focusMessage = async () => {
  await nextTick()
  document.getElementById('messageInput')?.focus()
}

defineExpose({ show, handleSSOSubscribe })

function fetchedSocialMedia (socialMediaData) {
  socialMedia.value = socialMediaData
}
</script>

<style lang="scss" scoped>
/** Empty comment for SCSS bug */
</style>
